import {AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, ViewChild} from '@angular/core';
import {FormGroup} from '@angular/forms';

@Component({
    selector: 'app-set-anchors-on-image',
    templateUrl: './set-anchors-on-image.component.html',
    styleUrls: ['./set-anchors-on-image.component.scss'],
    standalone: false
})
export class SetAnchorsOnImageComponent implements AfterViewInit {
  @Input() imageSrc: string;
  @Input() form: FormGroup;
  @ViewChild('imageContainer', {static: true}) imageContainer!: ElementRef;
  @Output() anchorPosition: EventEmitter<any> = new EventEmitter();

  pointX: number = 0;
  pointY: number = 0;
  isDragging: boolean = false;

  anchorX: number = 0;
  anchorY: number = 0;

  setPointThreshold = 0.03;
  releaseThreshold = 0.05;
  private snapped: {x: number; y: number} | null = null;

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.setPinPosition(this.form);
    }, 1000);
  }

  setPinPosition(form: FormGroup): void {
    const anchorX = form?.get('anchor_x')?.value;
    const anchorY = form?.get('anchor_y')?.value;

    const rect = this.imageContainer.nativeElement.getBoundingClientRect();
    const w = rect.width;
    const h = rect.height;

    if (anchorX !== null && anchorX !== undefined && anchorY !== null && anchorY !== undefined) {
      this.anchorX = this.clamp01(anchorX);
      this.anchorY = this.clamp01(anchorY);

      this.pointX = this.anchorX * w;
      this.pointY = this.anchorY * h;
    }
  }

  setPoint(event: MouseEvent): void {
    if (!this.isDragging) {
      this.updatePosition(event);
      this.emitAnchorPosition();
    }
  }

  startDrag(event: MouseEvent): void {
    this.isDragging = true;
  }

  stopDrag(): void {
    this.isDragging = false;
  }

  onMouseMove(event: MouseEvent): void {
    if (this.isDragging) {
      this.updatePosition(event);
    }
  }

  private updatePosition(event: MouseEvent): void {
    const rect = this.imageContainer.nativeElement.getBoundingClientRect();
    const w = rect.width;
    const h = rect.height;

    let px = event.clientX - rect.left;
    let py = event.clientY - rect.top;

    px = Math.max(0, Math.min(px, w));
    py = Math.max(0, Math.min(py, h));

    this.pointX = px;
    this.pointY = py;
    this.anchorX = px / w;
    this.anchorY = py / h;

    this.setCursorToPoints(w, h);
  }

  setCursorToPoints(containerWidth: number, containerHeight: number): void {
    // 9 common points
    const settingPoints = [
      {x: 0, y: 0}, // Top-left corner
      {x: 0.5, y: 0}, // Top-center
      {x: 1, y: 0}, // Top-right corner
      {x: 0, y: 0.5}, // Left-center
      {x: 0.5, y: 0.5}, // Center
      {x: 1, y: 0.5}, // Right-center
      {x: 0, y: 1}, // Bottom-left corner
      {x: 0.5, y: 1}, // Bottom-center
      {x: 1, y: 1}, // Bottom-right corner
    ];

    let x = this.anchorX;
    let y = this.anchorY;

    if (this.snapped) {
      const d = Math.hypot(x - this.snapped.x, y - this.snapped.y);
      if (d <= this.releaseThreshold) {
        x = this.snapped.x;
        y = this.snapped.y;
      } else {
        this.snapped = null;
      }
    }

    if (!this.snapped) {
      let best: {x: number; y: number} | null = null;
      let bestD = Infinity;
      for (const p of settingPoints) {
        const d = Math.hypot(x - p.x, y - p.y);
        if (d < this.setPointThreshold && d < bestD) {
          best = p;
          bestD = d;
        }
      }
      if (best) {
        this.snapped = best;
        x = best.x;
        y = best.y;
      }
    }

    this.anchorX = x;
    this.anchorY = y;
    this.pointX = x * containerWidth;
    this.pointY = y * containerHeight;
  }

  emitAnchorPosition(): void {
    this.anchorPosition.emit({
      anchorX: Math.max(0, parseFloat(this.anchorX.toFixed(2))),
      anchorY: Math.max(0, parseFloat(this.anchorY.toFixed(2))),
    });
  }

  private clamp01(v: number): number {
    return Math.min(1, Math.max(0, v));
  }
}
